/**
 * Demo host ↔ framed app handoff protocol (plan 2026-09-13-0029, KTD7).
 *
 * The presenter page on `demo.schooljourney.com` (parent, `apps/demo`) and the
 * mobile web export inside its same-origin iframe (frame, `apps/mobile`) talk
 * over `window.postMessage`, and nothing else. This module is the whole
 * vocabulary; both sides import it, so a message one side sends is by
 * construction one the other side parses.
 *
 * | Direction      | Message      | Payload                     | Rule                                               |
 * |----------------|--------------|-----------------------------|----------------------------------------------------|
 * | frame → parent | `ready`      | `{ hadSession? }`           | on load and after every `sign-out`                 |
 * | parent → frame | `ticket`     | `{ ticket, nonce, school }` | after the first `ready`; one in flight at a time   |
 * | frame → parent | `signed-in`  | `{ nonce, sessionId }`      | parent acks it to the backend, which verifies it   |
 * | parent → frame | `sign-out`   | —                           | frame logs out, posts `signed-out`, then `ready`   |
 * | frame → parent | `signed-out` | —                           | parent may clear its cookie or swap the `src`      |
 * | frame → parent | `error`      | `{ code, nonce? }`          | `TICKET_EXPIRED` triggers one automatic re-mint    |
 *
 * ONE TICKET IN FLIGHT. The frame accepts a `ticket` once it has posted its
 * first `ready`, and the next `ticket` once it has answered the previous one
 * with `signed-in` or `error` — a persona switch needs no fresh `ready`. A
 * `ticket` that arrives while another is still in flight is dropped with no
 * reply, so the parent must keep its own timeout rather than wait for an answer.
 *
 * THE FIRST `ready` REPORTS WHETHER THE FRAME BOOTED HOLDING A SESSION
 * (`hadSession`, plan 2026-09-14-1420 KTD1). Every `credentialless` iframe under
 * one top-level document shares one ephemeral cookie jar, cleared only when that
 * top-level page unloads. So on a page's FIRST frame an isolated jar is empty by
 * construction, and a session there proves the browser is sharing the parent's
 * jar — which the host uses to fall back (R4). It is only meaningful on that
 * first frame: a later frame may legitimately hold the previous persona's or the
 * presenter's own session, so the host never checks those. The `ready` that
 * follows a `sign-out` omits the field.
 *
 * WHAT THIS MODULE DOES NOT DO, AND EACH SIDE MUST. Parsing proves a message is
 * well-formed, not that it came from the right window. Every receiver also
 * checks `event.origin === location.origin` and the exact `event.source` (the
 * parent checks the iframe's `contentWindow`, the frame checks
 * `window.parent`), and every sender passes `targetOrigin = location.origin` —
 * never `'*'`. The ticket travels only in a message, never in a URL, fragment
 * or query string (R11).
 *
 * THE CHANNEL TAG. Every message carries `channel: 'sj-demo-handoff'`. The
 * frame's window also receives postMessage traffic from libraries that know
 * nothing about this protocol (auth SDK popups, dev tooling), and a tag makes
 * those fail the parse on the first field rather than on a coincidentally
 * shaped `type`. Build outgoing messages with {@link makeHandoffMessage} so the
 * tag is never forgotten.
 */

import { z } from 'zod'

/** Tag carried by every handoff message; anything without it is not ours. */
export const DEMO_HANDOFF_CHANNEL = 'sj-demo-handoff' as const

/** Path prefix the mobile web export is served under on the demo host (KTD4). */
export const DEMO_APP_BASE_PATH = '/app' as const

/**
 * The signed-out entry route the host loads in the frame, relative to
 * {@link DEMO_APP_BASE_PATH} — the frame's full URL is `/app/demo-handoff`. The
 * route only renders a loading state; the receiver itself lives at app-root
 * lifetime (KTD8).
 */
export const DEMO_HANDOFF_ROUTE = '/demo-handoff' as const

/**
 * The device frames the presenter page can draw. `ios` is the default.
 *
 * NOT PART OF THE WIRE PROTOCOL (SCH-1826). The frame type is the picture the
 * host draws AROUND the iframe — bezel, corner radius, camera cutout — and
 * nothing else. It never reaches the framed export: the app lays out at the
 * same size and under the same iOS safe-area insets in both wrappers, so
 * swapping the wrapper neither reloads the frame nor re-mints the persona.
 * It lives here because the host's own modules share it, not because the two
 * sides exchange it.
 */
export const DEMO_FRAME_TYPES = ['ios', 'android'] as const
export const DemoFrameTypeSchema = z.enum(DEMO_FRAME_TYPES)
export type DemoFrameType = z.infer<typeof DemoFrameTypeSchema>

/** The wrapper the host draws until the viewer picks another. */
export const DEFAULT_DEMO_FRAME_TYPE: DemoFrameType = 'ios'

/**
 * Correlates a `ticket` with the `signed-in` / `error` it produced, so the
 * parent can ignore a late answer to a ticket it has already given up on. It
 * binds nothing — a ticket cannot be device-bound — so any unguessable string
 * works; the host uses `crypto.randomUUID()`.
 */
export const DemoHandoffNonceSchema = z.string().min(16).max(128)

/** A Clerk session id, as the frame reads it off the session it just created. */
export const DemoClerkSessionIdSchema = z.string().regex(/^sess_[A-Za-z0-9]+$/, 'Expected a Clerk session id (sess_…)')

/**
 * The school the ticket signs into. Mirrors the dev-login mint response's
 * `school`, because the frame hands it to the same `sessionLogin` path the
 * dev-login picker uses to pre-seed the active school.
 */
export const DemoHandoffSchoolSchema = z.object({
  id: z.string().min(1).describe('School public id'),
  name: z.string().nullable()
})
export type DemoHandoffSchool = z.infer<typeof DemoHandoffSchoolSchema>

/**
 * Why the frame could not finish a `ticket` or `sign-out`.
 *
 * - `TICKET_EXPIRED` — Clerk refused the ticket as expired. The only code the
 *   parent retries on: it re-mints once, then surfaces the second failure.
 * - `TICKET_REJECTED` — Clerk refused the ticket for any other reason (already
 *   used, malformed). Retrying the same way would fail the same way.
 * - `SESSION_EXISTS` — a session survived the sign-out-by-id that precedes
 *   every redemption, so redeeming would fail or sign in as the wrong person.
 * - `PIPELINE_FAILED` — the ticket was redeemed but the app's session pipeline
 *   (`sessionLogin`) or the logout pipeline behind a `sign-out` failed.
 */
export const DEMO_HANDOFF_ERROR_CODES = [
  'TICKET_EXPIRED',
  'TICKET_REJECTED',
  'SESSION_EXISTS',
  'PIPELINE_FAILED'
] as const
export const DemoHandoffErrorCodeSchema = z.enum(DEMO_HANDOFF_ERROR_CODES)
export type DemoHandoffErrorCode = z.infer<typeof DemoHandoffErrorCodeSchema>

const channel = z.literal(DEMO_HANDOFF_CHANNEL)

// ---------------------------------------------------------------------------
// Parent → frame
// ---------------------------------------------------------------------------

export const DemoHandoffTicketMessageSchema = z.object({
  channel,
  type: z.literal('ticket'),
  ticket: z.string().min(1).max(4096).describe('Single-use Clerk sign-in ticket; never put it in a URL or a log'),
  nonce: DemoHandoffNonceSchema,
  school: DemoHandoffSchoolSchema
})

export const DemoHandoffSignOutMessageSchema = z.object({
  channel,
  type: z.literal('sign-out')
})

export const DemoHandoffParentMessageSchema = z.discriminatedUnion('type', [
  DemoHandoffTicketMessageSchema,
  DemoHandoffSignOutMessageSchema
])
export type DemoHandoffParentMessage = z.infer<typeof DemoHandoffParentMessageSchema>

// ---------------------------------------------------------------------------
// Frame → parent
// ---------------------------------------------------------------------------

export const DemoHandoffReadyMessageSchema = z.object({
  channel,
  type: z.literal('ready'),
  /**
   * Whether the Clerk client already held a session when the frame first
   * announced itself. Set only on the frame's first `ready`; omitted on the
   * `ready` that follows a `sign-out`, where a leftover session says nothing
   * about the jar. See the `hadSession` note at the top of this module.
   */
  hadSession: z.boolean().optional()
})

export const DemoHandoffSignedInMessageSchema = z.object({
  channel,
  type: z.literal('signed-in'),
  nonce: DemoHandoffNonceSchema,
  sessionId: DemoClerkSessionIdSchema
})

export const DemoHandoffSignedOutMessageSchema = z.object({
  channel,
  type: z.literal('signed-out')
})

export const DemoHandoffErrorMessageSchema = z.object({
  channel,
  type: z.literal('error'),
  code: DemoHandoffErrorCodeSchema,
  /** The failing ticket's nonce; absent when the failure was a `sign-out`. */
  nonce: DemoHandoffNonceSchema.optional()
})

export const DemoHandoffFrameMessageSchema = z.discriminatedUnion('type', [
  DemoHandoffReadyMessageSchema,
  DemoHandoffSignedInMessageSchema,
  DemoHandoffSignedOutMessageSchema,
  DemoHandoffErrorMessageSchema
])
export type DemoHandoffFrameMessage = z.infer<typeof DemoHandoffFrameMessageSchema>

// ---------------------------------------------------------------------------
// Either direction
// ---------------------------------------------------------------------------

export const DemoHandoffMessageSchema = z.discriminatedUnion('type', [
  DemoHandoffTicketMessageSchema,
  DemoHandoffSignOutMessageSchema,
  DemoHandoffReadyMessageSchema,
  DemoHandoffSignedInMessageSchema,
  DemoHandoffSignedOutMessageSchema,
  DemoHandoffErrorMessageSchema
])
export type DemoHandoffMessage = z.infer<typeof DemoHandoffMessageSchema>
export type DemoHandoffMessageType = DemoHandoffMessage['type']

/**
 * Parse any `MessageEvent.data` into a handoff message, or `null`.
 *
 * Never throws: a receiver sees every message posted to its window, most of
 * them not ours, and a throw on each would be noise at best. Prefer the
 * direction-specific {@link parseHandoffMessageFromParent} /
 * {@link parseHandoffMessageFromFrame} in a receiver — the frame has no business
 * acting on a `signed-in`, nor the parent on a `ticket`.
 */
export function parseHandoffMessage(data: unknown): DemoHandoffMessage | null {
  const parsed = DemoHandoffMessageSchema.safeParse(data)
  return parsed.success ? parsed.data : null
}

/** The frame's parser: only `ticket` and `sign-out` are accepted. */
export function parseHandoffMessageFromParent(data: unknown): DemoHandoffParentMessage | null {
  const parsed = DemoHandoffParentMessageSchema.safeParse(data)
  return parsed.success ? parsed.data : null
}

/** The parent's parser: only `ready`, `signed-in`, `signed-out` and `error` are accepted. */
export function parseHandoffMessageFromFrame(data: unknown): DemoHandoffFrameMessage | null {
  const parsed = DemoHandoffFrameMessageSchema.safeParse(data)
  return parsed.success ? parsed.data : null
}

/** Distributes `Omit` over the union, so each member keeps its own fields. */
type WithoutChannel<T> = T extends unknown ? Omit<T, 'channel'> : never

/**
 * Build an outgoing message with the channel tag attached:
 * `window.parent.postMessage(makeHandoffMessage({ type: 'ready' }), location.origin)`.
 */
export function makeHandoffMessage<T extends WithoutChannel<DemoHandoffMessage>>(
  body: T
): T & { channel: typeof DEMO_HANDOFF_CHANNEL } {
  return { ...body, channel: DEMO_HANDOFF_CHANNEL }
}
